import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { CheckmarkIcon, ChevronDownIcon, ChevronRightIcon, SearchIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import type { ComboboxOption } from "@/components/ui/combobox";

// ─── Column state context ─────────────────────────────────────────────────────

interface ColumnState {
  isActive: boolean;
}

const ColumnStateContext = createContext<ColumnState>({ isActive: true });

export function useColumnState(): ColumnState {
  return useContext(ColumnStateContext);
}

// ─── Primitives ───────────────────────────────────────────────────────────────

export function SectionHeader({
  icon,
  label,
}: {
  icon?: ReactNode;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-muted-foreground bg-muted border-b sticky top-0 z-10 uppercase tracking-wide shrink-0">
      {icon}
      <span className="truncate">{label}</span>
    </div>
  );
}

interface RowProps {
  isSelected?: boolean;
  hasError?: boolean;
  hasWarning?: boolean;
  hasChildren?: boolean;
  /** Tint the row to mark it as touched during the current editing session. */
  isTouched?: boolean;
  /** Dim and strike the row: queued for deletion, not yet saved. */
  isStruck?: boolean;
  onClick?: () => void;
  children: ReactNode;
}

export function Row({
  isSelected,
  hasError,
  hasWarning,
  hasChildren,
  isTouched,
  isStruck,
  onClick,
  children,
}: RowProps) {
  const { isActive } = useColumnState();
  const selected = !!isSelected && isActive;
  const parentSelected = !!isSelected && !isActive;
  const state = selected
    ? "bg-primary text-primary-foreground"
    : parentSelected
    ? "bg-primary/20 text-foreground"
    : hasError || hasWarning
    ? "bg-destructive/5 hover:bg-destructive/10"
    : isTouched
    ? "bg-sky-100 hover:bg-sky-200 dark:bg-sky-900/50 dark:hover:bg-sky-800/60"
    : "hover:bg-muted/50";
  return (
    <div
      data-selected={selected || parentSelected ? "true" : undefined}
      className={`flex items-center border-b gap-2 px-3 py-2 cursor-pointer select-none shrink-0 ${state}${
        isStruck ? " opacity-50 line-through" : ""
      }`}
      onClick={onClick}
    >
      <span className="flex-1 min-w-0">{children}</span>
      {hasChildren && (
        <ChevronRightIcon
          className={`h-3.5 w-3.5 shrink-0 ${
            selected ? "text-primary-foreground/60" : "text-muted-foreground/50"
          }`}
        />
      )}
    </div>
  );
}

export function DetailPanel({ children }: { children: ReactNode }) {
  return <div className="px-3 py-4 space-y-3 border-b">{children}</div>;
}

export function DetailField({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="text-xs text-foreground">{value}</div>
    </div>
  );
}

export function EmptyCol({ label }: { label?: string }) {
  return (
    <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground/40 p-4 text-center">
      {label}
    </div>
  );
}

// ─── CheckboxRow ──────────────────────────────────────────────────────────────

export function CheckboxRow({
  isSelected,
  checked,
  onChange,
  onClick,
  children,
}: {
  isSelected?: boolean;
  checked: boolean;
  onChange: (checked: boolean) => void;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <Row isSelected={isSelected} onClick={onClick}>
      <div className="flex items-center gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onChange(v === true)}
          className="pointer-events-none shrink-0"
        />
        <span className="text-xs truncate">{children}</span>
      </div>
    </Row>
  );
}

// ─── ComboboxRow ──────────────────────────────────────────────────────────────

export { type ComboboxOption };

export function ComboboxRow({
  isSelected,
  isActivated,
  onDeactivate,
  options,
  value,
  onChange,
  placeholder = "Valitse...",
  searchPlaceholder = "Hae...",
  onClick,
  children,
  disabled = false,
}: {
  isSelected?: boolean;
  isActivated: boolean;
  onDeactivate: () => void;
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  onClick?: () => void;
  children?: ReactNode;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = options.filter(
    (o) =>
      o.label.toLowerCase().includes(search.toLowerCase()) ||
      o.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightedIdx(0);
  }, [search]);

  // Focus input and reset when activated
  useEffect(() => {
    if (isActivated) {
      setSearch("");
      const selectedIdx = options.findIndex((o) => o.value === value);
      setHighlightedIdx(selectedIdx >= 0 ? selectedIdx : 0);
      setTimeout(() => {
        inputRef.current?.focus();
        const list = listRef.current;
        if (list) {
          const el = list.querySelector<HTMLElement>("[data-highlighted='true']");
          if (el) {
            const lRect = list.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            if (eRect.top < lRect.top) list.scrollTop -= lRect.top - eRect.top;
            else if (eRect.bottom > lRect.bottom) list.scrollTop += eRect.bottom - lRect.bottom;
          }
        }
      }, 0);
    }
  }, [isActivated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll highlighted option into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const highlighted = list.querySelector<HTMLElement>(
      "[data-highlighted='true']"
    );
    if (!highlighted) return;
    const listRect = list.getBoundingClientRect();
    const elRect = highlighted.getBoundingClientRect();
    if (elRect.top < listRect.top) list.scrollTop -= listRect.top - elRect.top;
    else if (elRect.bottom > listRect.bottom)
      list.scrollTop += elRect.bottom - listRect.bottom;
  }, [highlightedIdx]);

  // While the combobox is open, prevent Escape from reaching the dialog's
  // capture-phase listener (Radix registers at document with capture:true and
  // skips dismiss when event.defaultPrevented is true).
  useEffect(() => {
    if (!isActivated) return;
    function handleEscapeCapture(e: KeyboardEvent) {
      if (e.key === "Escape") e.preventDefault();
    }
    window.addEventListener("keydown", handleEscapeCapture, { capture: true });
    return () => window.removeEventListener("keydown", handleEscapeCapture, { capture: true });
  }, [isActivated]);

  // Close on click outside
  useEffect(() => {
    if (!isActivated) return;
    function handleMouseDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onDeactivate();
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isActivated, onDeactivate]);

  function selectOption(option: ComboboxOption) {
    onChange(option.value);
    onDeactivate();
    setSearch("");
  }

  function refocusAncestor() {
    let el: HTMLElement | null = containerRef.current?.parentElement ?? null;
    while (el) {
      if (el.tabIndex >= 0) {
        el.focus();
        break;
      }
      el = el.parentElement;
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      setHighlightedIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const opt = filtered[highlightedIdx];
      if (opt) {
        selectOption(opt);
        refocusAncestor();
      }
    } else if (e.key === "Escape") {
      e.stopPropagation();
      onDeactivate();
      setSearch("");
      refocusAncestor();
    }
  }

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div ref={containerRef} className="relative">
      <Row isSelected={isSelected} onClick={onClick}>
        <div className={cn("flex items-center gap-1.5 min-w-0", disabled && "opacity-50")}>
          {children}
          <ChevronDownIcon
            className={cn(
              "h-3 w-3 shrink-0",
              isSelected
                ? "text-primary-foreground/50"
                : "text-muted-foreground/40"
            )}
          />
          <span
            className={cn(
              "text-xs truncate flex-1",
              !selectedOption && "text-muted-foreground/60"
            )}
          >
            {selectedOption
              ? selectedOption.triggerLabel ?? selectedOption.label
              : placeholder}
          </span>
        </div>
      </Row>
      {isActivated && !disabled && (
        <div className="absolute left-0 right-0 z-50 rounded-md border bg-popover shadow-md">
          <div className="flex items-center border-b px-3">
            <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              className="flex h-9 w-full bg-transparent py-2 text-base md:text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div ref={listRef} className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Ei tuloksia
              </p>
            ) : (
              filtered.map((option, idx) => (
                <button
                  key={option.value}
                  type="button"
                  data-highlighted={idx === highlightedIdx}
                  onMouseEnter={() => setHighlightedIdx(idx)}
                  onClick={() => selectOption(option)}
                  className={cn(
                    "flex w-full items-center rounded-sm px-2 py-1.5 text-sm",
                    idx === highlightedIdx
                      ? "bg-accent text-accent-foreground"
                      : ""
                  )}
                >
                  <CheckmarkIcon
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="text-left">
                    <div>{option.label}</div>
                    {option.description && (
                      <div className="text-xs text-muted-foreground">
                        {option.description}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ColumnLayout ─────────────────────────────────────────────────────────────

export interface ColumnNavigation {
  length: number;
  selectedIndex: number | null;
  onSelectIndex: (index: number) => void;
  onClear?: () => void;
  onActivate?: (index: number, cellIndex?: number) => void;
  /** Optional 2D cell navigation within each row. When provided, ArrowLeft /
   *  ArrowRight move between cells (skipping disabled ones) before falling
   *  through to the column-level enter/exit behavior. */
  cells?: {
    selectedCellIndex: number;
    onSelectCellIndex: (cellIndex: number) => void;
    isCellEnabled: (rowIndex: number, cellIndex: number) => boolean;
    cellsPerRow: number;
  };
}

export interface ColumnDescriptor {
  key: string;
  width?: string;
  className?: string;
  navigation?: ColumnNavigation;
  content: ReactNode;
}

export interface ColumnLayoutProps {
  columns: ColumnDescriptor[];
  height?: string;
  enableHorizontalScroll?: boolean;
  className?: string;
  /** Increment to imperatively re-focus the container (e.g. when entering edit mode). */
  focusTrigger?: number;
}

function computeActiveIndex(columns: ColumnDescriptor[]): number {
  for (let i = columns.length - 1; i >= 0; i--) {
    if (columns[i].navigation?.selectedIndex != null) return i;
  }
  return 0;
}

export function ColumnLayout({
  columns,
  height,
  enableHorizontalScroll = true,
  className,
  focusTrigger,
}: ColumnLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const positionMemoryRef = useRef<Map<number, number>>(new Map());
  const activeColIndex = computeActiveIndex(columns);
  const selectionFingerprint = columns
    .map((c) => c.navigation?.selectedIndex ?? "null")
    .join("|");

  // Horizontal scroll: keep the column right of the active one visible.
  useEffect(() => {
    if (!enableHorizontalScroll) return;
    const container = containerRef.current;
    if (!container) return;
    const cols = Array.from(
      container.querySelectorAll<HTMLElement>(":scope > [data-column]")
    );
    const nextCol = cols[activeColIndex + 1];
    if (!nextCol) return;
    const nextRect = nextCol.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    container.scrollLeft += nextRect.right - containerRect.right;
  }, [selectionFingerprint, activeColIndex, enableHorizontalScroll]);

  // Vertical scroll: bring the selected row of each column into view.
  useEffect(() => {
    containerRef.current
      ?.querySelectorAll<HTMLElement>("[data-selected='true']")
      .forEach((el) => {
        const col = el.closest<HTMLElement>("[data-column]");
        if (!col) return;
        const elRect = el.getBoundingClientRect();
        const colRect = col.getBoundingClientRect();
        const stickyHeader = col.querySelector<HTMLElement>(".sticky");
        const headerHeight = stickyHeader
          ? stickyHeader.getBoundingClientRect().height
          : 0;
        const firstRow = col.querySelector<HTMLElement>(".cursor-pointer");
        if (el === firstRow) {
          const rowOffsetTop = col.scrollTop + elRect.top - colRect.top;
          const rowHeight = elRect.bottom - elRect.top;
          const colHeight = colRect.bottom - colRect.top;
          // Scroll to top, clamped so the row's bottom stays within the column
          col.scrollTop = Math.max(0, rowOffsetTop + rowHeight - colHeight);
          return;
        }
        if (elRect.top < colRect.top + headerHeight) {
          col.scrollTop -= colRect.top + headerHeight - elRect.top;
        } else if (elRect.bottom > colRect.bottom) {
          col.scrollTop += elRect.bottom - colRect.bottom;
        }
      });
  }, [selectionFingerprint]);

  // Keyboard navigation.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function handleKey(e: KeyboardEvent) {
      if (
        ![
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Enter",
          " ",
        ].includes(e.key)
      ) {
        return;
      }
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
      e.preventDefault();

      const active = columns[activeColIndex];
      const nav = active?.navigation;

      if (e.key === "Enter" || e.key === " ") {
        if (nav && nav.selectedIndex != null) {
          nav.onActivate?.(nav.selectedIndex, nav.cells?.selectedCellIndex);
        }
        return;
      }

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        const dir = e.key === "ArrowDown" ? 1 : -1;
        if (!nav || nav.length === 0) return;
        if (nav.selectedIndex == null) {
          nav.onSelectIndex(dir === 1 ? 0 : nav.length - 1);
          return;
        }
        const next = nav.selectedIndex + dir;
        if (next < 0 || next >= nav.length) return;
        nav.onSelectIndex(next);
        for (let i = activeColIndex + 1; i < columns.length; i++) {
          positionMemoryRef.current.delete(i);
        }
        // Preserve cell index; re-target to nearest enabled cell if landing
        // on a disabled cell (search same → right → left).
        if (nav.cells) {
          const cells = nav.cells;
          const curr = cells.selectedCellIndex;
          if (!cells.isCellEnabled(next, curr)) {
            let found = -1;
            for (let d = 1; d < cells.cellsPerRow; d++) {
              if (
                curr + d < cells.cellsPerRow &&
                cells.isCellEnabled(next, curr + d)
              ) {
                found = curr + d;
                break;
              }
              if (curr - d >= 0 && cells.isCellEnabled(next, curr - d)) {
                found = curr - d;
                break;
              }
            }
            if (found !== -1) cells.onSelectCellIndex(found);
          }
        }
        return;
      }

      if (e.key === "ArrowRight") {
        if (!nav || nav.selectedIndex == null) return;
        if (nav.cells) {
          const cells = nav.cells;
          for (let i = cells.selectedCellIndex + 1; i < cells.cellsPerRow; i++) {
            if (cells.isCellEnabled(nav.selectedIndex, i)) {
              cells.onSelectCellIndex(i);
              return;
            }
          }
          // No enabled cell to the right — fall through to next-column entry.
        }
        const nextColIdx = activeColIndex + 1;
        const nextCol = columns[nextColIdx];
        const nextNav = nextCol?.navigation;
        if (nextNav && nextNav.length > 0 && nextNav.selectedIndex == null) {
          const saved = positionMemoryRef.current.get(nextColIdx);
          const startIdx = saved != null && saved < nextNav.length ? saved : 0;
          nextNav.onSelectIndex(startIdx);
        }
        return;
      }

      // ArrowLeft — move between cells first, then exit the column.
      if (nav?.cells && nav.selectedIndex != null) {
        const cells = nav.cells;
        for (let i = cells.selectedCellIndex - 1; i >= 0; i--) {
          if (cells.isCellEnabled(nav.selectedIndex, i)) {
            cells.onSelectCellIndex(i);
            return;
          }
        }
        // No enabled cell to the left — fall through to column exit.
      }
      if (activeColIndex === 0) return;
      if (nav && nav.selectedIndex != null) {
        positionMemoryRef.current.set(activeColIndex, nav.selectedIndex);
      }
      nav?.onClear?.();
    }

    el.addEventListener("keydown", handleKey);
    return () => el.removeEventListener("keydown", handleKey);
  }, [columns, activeColIndex]);

  // Auto-select the first item of the first column whenever it has navigable
  // items but no selection. The first column is always "occupied".
  useEffect(() => {
    const nav = columns[0]?.navigation;
    if (nav && nav.length > 0 && nav.selectedIndex == null) {
      nav.onSelectIndex(0);
    }
  });

  // Focus the container on mount and whenever focusTrigger increments.
  useEffect(() => {
    containerRef.current?.focus();
  }, [focusTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const overflowClass = enableHorizontalScroll
    ? "overflow-x-auto overflow-y-hidden"
    : "overflow-hidden";
  const style: CSSProperties | undefined = height ? { height } : undefined;

  return (
    <div
      ref={containerRef}
      className={`flex bg-background focus:outline-none ${overflowClass} ${
        className ?? ""
      }`}
      tabIndex={0}
      style={style}
    >
      {columns.map((col, idx) => (
        <ColumnStateContext.Provider
          key={col.key}
          value={{ isActive: idx === activeColIndex }}
        >
          <div
            data-column=""
            className={`flex flex-col overflow-y-auto border-r last:border-r-0 shrink-0 ${
              col.width ?? "w-56"
            } ${col.className ?? ""}`}
          >
            {col.content}
          </div>
        </ColumnStateContext.Provider>
      ))}
    </div>
  );
}
